import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Shield, Search, Save, Loader2, AlertTriangle, Plus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const SEV = { info: "bg-[#144F75]/10 text-[#144F75]", concern: "bg-[#D98E73]/15 text-[#B5794E]", violation: "bg-red-100 text-red-600" };

export default function AccountabilityPanel() {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const users = useQuery({ queryKey: ["acc-users", q], queryFn: () => api.get(`/accountability/users?q=${encodeURIComponent(q)}`).then((r) => r.data) });

  return (
    <div data-testid="accountability-panel">
      <div className="flex items-center gap-2 mb-1"><Shield size={20} className="text-[#144F75]" /><h2 className="font-serif text-2xl text-[#1A242B]">Internal Accountability</h2></div>
      <p className="text-sm text-[#4A5D6B] mb-6">Private conduct scores &amp; notes — visible only to Council &amp; Admin. Never shown publicly.</p>
      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6">
        <div>
          <div className="relative mb-3">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8FA89B]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members…" data-testid="acc-search" className="eyw-input w-full pl-9" />
          </div>
          <div className="eyw-card divide-y divide-[#E5E0D8] max-h-[560px] overflow-y-auto">
            {(users.data || []).map((u) => (
              <button key={u.id} onClick={() => setSelected(u.id)} data-testid={`acc-user-${u.id}`}
                className={`w-full text-left p-3 flex items-center justify-between ${selected === u.id ? "bg-[#F1EDE6]" : "hover:bg-[#F9F7F3]"}`}>
                <div>
                  <div className="text-sm font-medium text-[#1A242B]">{u.name}</div>
                  <div className="text-xs text-[#8FA89B]">{u.role} · {u.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  {u.flags > 0 && <span className="text-xs text-red-500 flex items-center gap-0.5"><AlertTriangle size={11} /> {u.flags}</span>}
                  {u.overall != null && <span className="text-sm font-serif text-[#144F75]">{u.overall}</span>}
                </div>
              </button>
            ))}
            {users.data?.length === 0 && <p className="text-[#4A5D6B] text-sm p-4">No members found.</p>}
          </div>
        </div>
        {selected ? <ScoreDetail userId={selected} onChange={() => users.refetch()} /> : <div className="eyw-card p-8 grid place-items-center text-[#8FA89B] text-sm">Select a member to view or set their accountability score.</div>}
      </div>
    </div>
  );
}

const Slider = ({ label, k, s, set }) => (
  <div><div className="flex justify-between text-sm mb-1"><span className="text-[#4A5D6B]">{label}</span><span className="font-medium text-[#144F75]">{s[k]}</span></div>
    <input type="range" min="1" max="5" value={s[k]} onChange={(e) => set(k, parseInt(e.target.value))} data-testid={`acc-${k}`} className="w-full accent-[#144F75]" /></div>
);

function ScoreDetail({ userId, onChange }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["acc-score", userId], queryFn: () => api.get(`/accountability/scores/${userId}`).then((r) => r.data) });
  const [form, setForm] = useState(null);
  const [note, setNote] = useState("");
  const [severity, setSeverity] = useState("info");
  const [saving, setSaving] = useState(false);

  if (isLoading || !data) return <div className="eyw-card p-8 text-[#4A5D6B]">Loading…</div>;
  const s = form || {
    reliability: data.score?.reliability ?? 3, professionalism: data.score?.professionalism ?? 3,
    values_alignment: data.score?.values_alignment ?? 3, notes: data.score?.notes ?? "",
  };
  const set = (k, v) => setForm({ ...s, [k]: v });
  const save = async () => {
    setSaving(true);
    try { const { data: r } = await api.put(`/accountability/scores/${userId}`, s); toast.success(`Saved — overall ${r.overall}`); qc.invalidateQueries({ queryKey: ["acc-score", userId] }); onChange?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/accountability/scores/${userId}/notes`, { note, severity }); toast.success("Note added"); setNote(""); qc.invalidateQueries({ queryKey: ["acc-score", userId] }); onChange?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sp = { s, set };
  return (
    <div className="eyw-card p-6" data-testid="acc-score-detail">
      <h3 className="font-serif text-xl text-[#1A242B]">{data.subject.name} <span className="text-sm text-[#8FA89B]">({data.subject.role})</span></h3>
      <div className="space-y-3 mt-4">
        <Slider {...sp} label="Reliability" k="reliability" />
        <Slider {...sp} label="Professionalism" k="professionalism" />
        <Slider {...sp} label="Values alignment" k="values_alignment" />
        <div><label className="text-sm font-medium mb-1 block">Internal summary</label>
          <textarea rows={2} value={s.notes} onChange={(e) => set("notes", e.target.value)} data-testid="acc-notes" className="eyw-input w-full resize-none" /></div>
        <div className="flex justify-end"><button onClick={save} disabled={saving} data-testid="acc-save" className="eyw-btn !py-2 text-sm disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={15} />} <Save size={14} /> Save score</button></div>
      </div>
      <div className="mt-6 border-t border-[#E5E0D8] pt-4">
        <h4 className="text-sm font-semibold text-[#1A242B] mb-3">Note log</h4>
        <div className="flex gap-2 mb-3">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" data-testid="acc-note-input" className="eyw-input flex-1" />
          <select value={severity} onChange={(e) => setSeverity(e.target.value)} data-testid="acc-note-severity" className="eyw-input"><option value="info">info</option><option value="concern">concern</option><option value="violation">violation</option></select>
          <button onClick={addNote} data-testid="acc-note-add" className="eyw-btn !py-2 !px-3 text-sm"><Plus size={15} /></button>
        </div>
        <div className="space-y-2 max-h-56 overflow-y-auto">
          {(data.notes || []).map((n) => (
            <div key={n.id} className="text-sm p-2 rounded-lg bg-[#F9F7F3] flex items-start justify-between gap-2">
              <div><span className={`text-[10px] px-1.5 py-0.5 rounded-full ${SEV[n.severity] || SEV.info}`}>{n.severity}</span> <span className="text-[#4A5D6B]">{n.note}</span></div>
              <span className="text-[10px] text-[#8FA89B] shrink-0">{n.author_name}</span>
            </div>
          ))}
          {data.notes?.length === 0 && <p className="text-xs text-[#8FA89B]">No notes yet.</p>}
        </div>
      </div>
    </div>
  );
}
